import type { BookingPaymentChoice } from './supportAssistantTypes';
import {
  extractPriceQuery,
  extractServiceQuery,
  isGenericCatalogQuery,
  normalizeServiceQuery,
  parsePaymentFromText,
} from './supportAssistantData';
import { containsAny, isGenericServicePhrase, isNo, isYes, PHRASES, startsWithAny } from './supportAssistantLocale';

export type SocIntentType =
  | 'book'
  | 'track'
  | 'cancel'
  | 'order_status'
  | 'price'
  | 'services'
  | 'addresses'
  | 'payment'
  | 'rebook'
  | 'support'
  | 'complaint'
  | 'explore'
  | 'faq'
  | 'greeting'
  | 'thanks'
  | 'reset'
  | 'unknown';

export type SocIntent = {
  type: SocIntentType;
  raw: string;
  serviceQuery?: string;
  priceQuery?: string;
  orderIndex?: number;
  orderId?: string;
  paymentChoice?: BookingPaymentChoice;
  cancelReason?: string;
};

/** Pull service name from multilingual booking phrases. */
export function extractInlineServiceMention(text: string): string | null {
  const patterns = [
    /\bmy\s+(.+?)\s+booking\b/i,
    /\b(?:for|about)\s+(.+?)\s+booking\b/i,
    /\bcancel\s+(?:my\s+)?(.+?)\s+(?:booking|order|appointment)\b/i,
    /\b(?:track|check|status of)\s+(?:my\s+)?(.+?)\s+(?:booking|order)\b/i,
    /\bwhere\s+is\s+(?:my\s+)?(.+?)\s+(?:booking|order|service)\b/i,
    /(?:meri|mera|nanna|en|naa|ningalude|tumchi|tamari)\s+(.+?)\s+(?:booking|order)/i,
    /(.+?)\s+(?:book karo|book madi|book cheyyi|book pannunga|book karna hai)/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m?.[1]) {
      const q = normalizeServiceQuery(m[1]);
      if (q.length >= 2 && !isGenericCatalogQuery(q)) return q;
    }
  }
  return null;
}

export function parseSocIntent(text: string): SocIntent {
  const raw = text.trim();
  const lower = raw.toLowerCase();
  if (!raw) return { type: 'unknown', raw };

  if (containsAny(raw, PHRASES.reset)) return { type: 'reset', raw };
  if (startsWithAny(raw, PHRASES.greeting) || containsAny(raw, ['namaste', 'namaskar', 'namaskara'])) {
    return { type: 'greeting', raw };
  }
  if (startsWithAny(raw, PHRASES.thanks)) return { type: 'thanks', raw };

  const paymentChoice = parsePaymentFromText(raw) ?? undefined;
  const serviceFromBook = extractServiceQuery(raw);
  const inlineService = extractInlineServiceMention(raw);
  const priceQuery = extractPriceQuery(raw);
  const serviceQuery = serviceFromBook
    ? normalizeServiceQuery(serviceFromBook)
    : inlineService ?? undefined;

  const orderIndexMatch =
    raw.match(/\border\s*#?(\d{1,2})\b/i) ??
    raw.match(/\b(?:cancel|track|check|status)\s+(?:order\s*)?#?(\d{1,2})\b/i) ??
    raw.match(/\b(\d{1,2})\s+(?:number|no|wala)\s+(?:order|booking)\b/i);
  const orderIndex = orderIndexMatch ? Number(orderIndexMatch[1]) : undefined;
  const orderIdMatch = raw.match(/\b([a-f0-9-]{8,36})\b/i);
  const orderId = orderIdMatch?.[1];

  if (/\b(open explore|browse services|discover services)\b/i.test(lower)) {
    return { type: 'explore', raw };
  }

  if (containsAny(raw, PHRASES.rebook)) {
    return { type: 'rebook', raw, serviceQuery };
  }

  if (containsAny(raw, PHRASES.cancel)) {
    const reasonMatch = raw.match(/\b(?:because|kyunki|ke liye|reason|karana)\s+(.+)$/i);
    return {
      type: 'cancel',
      raw,
      serviceQuery,
      orderIndex,
      orderId,
      cancelReason: reasonMatch?.[1]?.trim(),
    };
  }

  if (containsAny(raw, PHRASES.track)) {
    return { type: 'track', raw, serviceQuery, orderIndex, orderId };
  }

  if (orderIndex != null || orderId || (/\bstatus\b/i.test(lower) && /\b(order|booking)\b/i.test(lower))) {
    return { type: 'order_status', raw, serviceQuery, orderIndex, orderId };
  }

  if (priceQuery || containsAny(raw, PHRASES.price)) {
    return { type: 'price', raw, priceQuery: priceQuery ?? serviceQuery, serviceQuery };
  }

  if (containsAny(raw, PHRASES.payment)) {
    return { type: 'payment', raw, paymentChoice, orderIndex, serviceQuery };
  }

  if (containsAny(raw, PHRASES.addresses)) {
    return { type: 'addresses', raw };
  }

  if (
    /\b(?:yava|yaava|enu|which|what)\b.*\bservices?\b/i.test(lower) ||
    /\bservices?\s+(?:ide|idhe|idave|available)\b/i.test(lower) ||
    /(?:ಯಾವ|ಏನು).*(?:ಸೇವೆ|ಸರ್ವಿಸ್)/i.test(raw) ||
    /(?:ಸೇವೆ|ಸರ್ವಿಸ್).*(?:ಇದೆ|ಇವೆ|ಲಭ್ಯ)/i.test(raw)
  ) {
    return { type: 'services', raw, serviceQuery };
  }

  if (containsAny(raw, PHRASES.services)) {
    return { type: 'services', raw, serviceQuery };
  }

  if (/\bhow\s+(?:do|can|to)\s+i\b/i.test(lower) || /\bwhat is\b/i.test(lower) || /\bkya hai\b/i.test(lower) || /\benu\b/i.test(lower)) {
    return { type: 'faq', raw, serviceQuery };
  }

  if (
    serviceFromBook ||
    containsAny(raw, PHRASES.book) ||
    (serviceQuery && !isGenericCatalogQuery(serviceQuery))
  ) {
    if (isGenericServicePhrase(raw) && !serviceFromBook) {
      return { type: 'book', raw, serviceQuery: '', paymentChoice };
    }
    if (serviceFromBook || (serviceQuery && !isGenericCatalogQuery(serviceQuery))) {
      return { type: 'book', raw, serviceQuery, paymentChoice };
    }
    if (containsAny(raw, PHRASES.book)) {
      return { type: 'book', raw, serviceQuery: serviceQuery ?? '', paymentChoice };
    }
  }

  if (containsAny(raw, PHRASES.complaint)) {
    return { type: 'complaint', raw, serviceQuery };
  }

  if (containsAny(raw, PHRASES.support)) {
    return { type: 'support', raw };
  }

  if (
    /\b(how|what|why|when|where|who|can i|does|do you|is it|are they|will i|policy|verified|secure|professional)\b/i.test(lower) ||
    raw.includes('?') ||
    /\b(tell me about|explain|information about|batao|bataiye|heli|helu|cheppandi|sollunga)\b/i.test(lower)
  ) {
    return { type: 'faq', raw, serviceQuery };
  }

  if (serviceQuery && !isGenericCatalogQuery(serviceQuery) && serviceQuery.length >= 3) {
    return { type: 'book', raw, serviceQuery, paymentChoice };
  }

  return { type: 'unknown', raw, serviceQuery };
}

export function isSocOperationalIntent(text: string): boolean {
  const intent = parseSocIntent(text);
  return (
    intent.type !== 'unknown' &&
    intent.type !== 'faq' &&
    intent.type !== 'greeting' &&
    intent.type !== 'thanks'
  );
}

export function isFlowContinuation(text: string, flow: string): boolean {
  if (flow !== 'idle') return true;
  const trimmed = text.trim();
  if (/^#?\d{1,3}$/.test(trimmed)) return true;
  if (isYes(trimmed) || isNo(trimmed)) return true;
  if (/^(more|skip|default)$/i.test(trimmed)) return true;
  return isSocOperationalIntent(text);
}

export { isYes, isNo };
